import * as React from 'react'
import {
  Block,
  VariantSetBlock,
  isComponentBlock,
  isListBlock,
  isReferenceBlock,
  isVariantSetBlock,
  type InstanceBlock,
} from '@basehub/data/replicache/index'

import { Root } from '../../properties-panel'
import * as PropertiesPanel from '../../properties-panel'
import { DocumentSidebarPortal } from '../../document-sidebar-portal'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { ChevronDownIcon, Component1Icon } from '@radix-ui/react-icons'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { useBlockReferences } from '~/hooks-data/use-block-referenced'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { HiddenTogglePropertiesPanel } from '../document/properties-panel'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { invariant } from '@epic-web/invariant'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { useEditorStore } from '../../editor-store'
import { useRepoStore } from '../../repo-store'
import { SelectorResultBlock } from '~/sotano-kit/primitives/selector'
import { useEditorMode } from '../../editor-mode-provider'

type Props = {
  block: InstanceBlock
  path: string
  parent: Block
  strict: boolean
}

function placeholder() {
  return null
}

export const InstancePropertiesPanel = ({ block, path, parent }: Props) => {
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()

  const templateBlock = blockTreeManager?.getNode(
    block.typeOptions.targetBlockId,
    { fallbackKey: 'sourceBlockId' }
  )

  const templateBlockParent = templateBlock?.parentKey
    ? blockTreeManager?.getNode(templateBlock.parentKey)
    : null

  const displayData = React.useMemo(() => {
    if (!templateBlock || !blockTreeManager) return undefined
    if (isComponentBlock(templateBlock.value)) {
      return getBlockDisplayData({
        block: templateBlock.value,
        parentBlock: templateBlockParent?.value,
      })
    } else {
      return {
        displayTitle: templateBlock.value.title || 'Untitled',
        displayDescription: undefined,
      }
    }
  }, [blockTreeManager, templateBlock, templateBlockParent?.value])

  const setsInUse = React.useMemo(() => {
    if (
      !blockTreeManager ||
      !templateBlock ||
      !isComponentBlock(templateBlock.value)
    ) {
      return null
    }

    const sets: VariantSetBlock[] = []
    for (const cId of templateBlock.value.constraints.acceptedVariantSets ??
      []) {
      const node = blockTreeManager.getNode(cId, {
        fallbackKey: 'sourceBlockId',
      })
      if (!node || !isVariantSetBlock(node.value)) {
        continue
      }
      sets.push(node.value)
    }
    return sets
  }, [blockTreeManager, templateBlock])
  const sourceBlockId = idToSourceBlockId(block)
  const setFocus = useRepoStore((s) => s.setFocus)

  return (
    <>
      <PropertiesPanel.PropertiesContainer>
        <HiddenTogglePropertiesPanel
          block={block}
          path={path}
          parent={parent}
        />
        <PropertiesPanel.Group title="Main component" spacing="loose">
          <p className="text-dark-muted text-xs -mt-1">
            This document is an instance of the following component:
          </p>
          <ButtonLink
            title={displayData?.displayTitle}
            intent="secondary"
            icon={<Component1Icon width={14} height={14} />}
            href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${
              templateBlock ? idToSourceBlockId(templateBlock.value) : ''
            }`}
          >
            <span className="line-clamp-1">{displayData?.displayTitle}</span>
          </ButtonLink>
          {!!templateBlock?.value && isComponentBlock(templateBlock?.value) && (
            <PropertiesPanel.ImageOrVideoOption
              name="Preview"
              id={`el-${sourceBlockId}-pp-preview`}
              value={templateBlock?.value.typeOptions?.displayImage ?? null}
              tooltipText="Preview of how the component looks in the actual website"
            />
          )}
          {displayData?.displayDescription && (
            <span className="text-[13px] text-dark-muted">
              {displayData.displayDescription}
            </span>
          )}
        </PropertiesPanel.Group>

        <PropertiesPanel.CommonSearchConfigGroup
          block={block}
          parent={parent}
          path={path}
          label="Index block title"
          tooltipText={`Inherits from Main Component. If checked, this block's title will be indexed into BaseHub Search. If unchecked, this block's title will not be included in the index.`}
        />
        {!!setsInUse?.length && (
          <PropertiesPanel.Group spacing="tight" title="Variants">
            <PropertiesPanel.SelectorOption<PropertiesPanel.PropertiesPanelComboboxOption>
              id={`${idToSourceBlockId(block)}-pp-variants`}
              emptyStateLabel="No variant set found"
              selected={setsInUse?.map((set) => ({
                value: idToSourceBlockId(set),
                label: set.title || 'Untitled',
              }))}
              onSelect={placeholder}
              renderResult={(search) => (
                <SelectorResultBlock
                  emptyStateLabel="Select variant set"
                  onSelect={placeholder}
                  search={search}
                  multiple
                  teamSlug={teamSlug}
                  repoSlug={repoSlug}
                  repoStore={{ setFocus }}
                  targetBlocks={setsInUse ?? []}
                />
              )}
              options={() => {
                return []
              }}
            />
          </PropertiesPanel.Group>
        )}
        <ReferencedByGroup blockId={block.id} />
      </PropertiesPanel.PropertiesContainer>
    </>
  )
}

export const ReferencedByGroup = ({ blockId }: { blockId: string }) => {
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const { encodedSegment } = useBranchOrCommitSegment()
  const editorMode = useEditorMode()
  const references = useBlockReferences({
    blockId: blockTreeManager
      ? idToSourceBlockId(blockId, blockTreeManager)
      : '',
  })
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const [seeAll, setSeeAll] = React.useState(false)
  const setFocus = useRepoStore((s) => s.setFocus)
  const refsCount = references.length

  if (editorMode !== 'write:all' && refsCount === 0) return null
  return (
    <PropertiesPanel.Group title={`Referenced by (${refsCount})`}>
      {references.slice(0, !seeAll ? 5 : undefined).map((refNode) => {
        invariant(refNode.parentKey, 'parentKey is required')
        invariant(blockTreeManager, 'blockTreeManager is required')
        let sourceParentRefNodeId
        try {
          sourceParentRefNodeId = idToSourceBlockId(
            refNode.parentKey,
            blockTreeManager
          )
        } catch (e) {
          sourceParentRefNodeId = refNode.parentKey
        }

        return (
          <PropertiesPanel.LinkOption
            key={refNode.key}
            label={
              refNode.value.title !== '' ? refNode.value.title : 'Untitled'
            }
            icon={<BlockIcon block={{ type: 'reference' }} />}
            href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${sourceParentRefNodeId}#${idToSourceBlockId(refNode.value)}`}
            onClick={() => {
              setFocus({
                type: 'block:wrapper',
                blockId: idToSourceBlockId(refNode.value),
              })
            }}
          />
        )
      })}
      {refsCount > 5 && (
        <button
          className="inline-flex max-w-max items-center gap-1 text-xs text-dark-faint"
          name="see all collections"
          onClick={() => setSeeAll(!seeAll)}
        >
          {seeAll ? 'See less' : 'See all'}
          <ChevronDownIcon
            className={clsx('', {
              'mt-px -rotate-180 transform': seeAll,
            })}
          />
        </button>
      )}
      {refsCount === 0 && (
        <PropertiesPanel.Paragraph>
          No references to this block.
        </PropertiesPanel.Paragraph>
      )}
    </PropertiesPanel.Group>
  )
}

export const FullViewInstancePropertiesPanel = (props: Props) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)

  const selectedBlockId = useEditorStore((s) => s.selectedBlockId)
  let isSelected = selectedBlockId === idToSourceBlockId(props.block)
  if (!props.strict && !isSelected) {
    isSelected =
      !selectedBlockId || getNode?.(selectedBlockId)?.value.type === 'comment'
  }

  return (
    <>
      <DocumentSidebarPortal hidden={!isSelected}>
        <Root block={props.block} parentBlock={props.parent ?? null}>
          <PropertiesPanel.BlockTitleGroup
            forceEditable={
              props.parent
                ? isListBlock(props.parent) || isReferenceBlock(props.parent)
                : false
            }
            block={props.block}
            path={props.path}
          />

          <InstancePropertiesPanel {...props} />
        </Root>
      </DocumentSidebarPortal>
    </>
  )
}
